import {
  DerivedAvailabilityStatus,
  DerivedAvailabilityStatusInternal,
  ProductType,
  UserSegment,
} from '@commerce-product-webcomponents/sdk';
import {
  FAIcon,
  IconStyle,
  Tag,
  TagStyle,
  TagWidth,
  Theme,
  UserType,
  useSession,
  useTranslation,
} from '@nextgen-web-framework/all';
import { FC, useEffect, useState } from 'react';
import {
  BasicDetailsComponent,
  ConditionalPriceRangeComponent,
  ConditionalRetailPriceRangeComponent,
  DerivedAvailabilityStatusComponentGroupKey,
  DetailsType,
  DropdownButton,
  DropdownButtonTypeEnum,
  IdAndSizeType,
  ImageComponent,
  ImageType,
  PVBVComponent,
  PVBVType,
  PriceComponent,
  PriceType,
  ProductButton,
  ProductButtonType,
  ProductIdAndSizeComponent,
  ProductQuantityComponent,
  ProductQuantityType,
  StockStatusComponent,
  StockStatusComponentType,
  includesDerivedAvailabilityStockStatus,
} from '..';
import { keyCheck } from '../../../utils';
import { ProductLink } from '../../Hoc';
import { calculateTheme, getTagLabel } from '../../SearchCard/searchCardUtils';
import { UserTypeEnums } from '../../SearchCard/types/enums';
import styles from './ProductCardComponent.module.scss';
import { CardType } from './types/enums';
import type {
  BasicDetailsAndSizeType,
  ImageFn,
  ProductCardComponentPropsTypes,
  PDPHeaderCardType,
  PromotionCardType,
  FreeProductCardType,
  CheckoutCardType,
  QtyStepperType,
  NormalCardWithoutSizeType,
  DropdownType,
  NormalCardType,
  ProductMerchandiseType,
} from './types/propsTypes';

const PDPHeaderCardView = ({
  productId,
  stockStatusEnabled,
  image,
  basicDetailsAndSize,
  stockStatusComp,
  productQuantity,
  className,
}: PDPHeaderCardType) => {
  const idSizeType = productId ? IdAndSizeType.IDSIZEPRODUCTCARD : IdAndSizeType.IDSIZECART;
  return (
    <div
      className={`${styles['cpw-product-card-container']} ${styles['pdp-header-card-view']} ${
        stockStatusEnabled && styles['height-extension']
      } ${className}`}
      data-testid="pdp-header-container-testID"
    >
      <div className={styles['product-image']} data-testid="Normal-image-testID">
        {image(ImageType.PLPHEADERCARD)}
      </div>
      <div className={styles['product-description']} data-testid="Normal-description-testID">
        {basicDetailsAndSize(DetailsType.PDP_HEADER_CARD, PVBVType.PVBVPDP, idSizeType)}
        {!isNaN(productQuantity as number) && (
          <ProductQuantityComponent
            productQuantity={productQuantity}
            productQuantityType={DetailsType.PDP_HEADER_CARD}
          />
        )}
        {stockStatusComp}
      </div>
    </div>
  );
};

const PromotionCardView = ({
  image,
  basicDetailsAndSize,
  stockStatusComp,
  className = '',
  buttonEle,
}: PromotionCardType) => (
  <div
    className={`${styles['cpw-product-card-container']} ${styles['promotion-card-container-height']} ${styles['promotion-view']} ${className}`}
    data-testid="PromotionCard-container-testID"
  >
    <div
      className={`${styles['product-image']} ${styles['promotion-card-image-height']}`}
      data-testid="PromotionCard-image-testID"
    >
      {image('')}
    </div>
    <div
      className={`${styles['product-description']} ${styles['dropdown-promotion-card-tablet-width']}`}
      data-testid="PromotionCard-description-testID"
    >
      {basicDetailsAndSize(
        DetailsType.AllPRODUCTCARD,
        PVBVType.PVBVPDP,
        IdAndSizeType.IDSIZEPROMOTION,
        PriceType.PROMOTION_CARD,
      )}
      {stockStatusComp}
      {buttonEle}
    </div>
  </div>
);

const FreeProductCardView = ({
  image,
  basicDetails,
  showProductId,
  productSize,
  productId,
  productPrice,
  currency,
  strikethroughPrice,
  productQuantity,
  onRemoveProduct,
  showCancelIcon,
  prefixText = '',
  className = '',
  pointValue = '',
  businessVolume = '',
  showZeroAsFree,
  derivedAvailabilityStatus,
  dateSubText,
  firstAvailableFutureDate,
  showRemoveIcon,
}: FreeProductCardType) => {
  const handleKeyDownRemove = (e: React.KeyboardEvent) => {
    if (keyCheck(e.code)) {
      onRemoveProduct?.(productId);
    }
  };
  return (
    <div
      className={`${styles['cpw-product-card-container']} ${styles['free-product-card-view']} ${className}`}
      data-testid="freeProduct-container-testID"
    >
      <div
        className={`${styles['product-image']} ${styles['free-product-image-height']}`}
        data-testid="freeProduct-image-testID"
      >
        {image(ImageType.CART_CARD)}
      </div>
      <div className={styles['product-description']}>
        <div className={styles['basic-product-info-description']}>
          <div className={styles['basic-size-info']}>
            {basicDetails(DetailsType.FREE_PRODUCT_CARD)}
            <ProductIdAndSizeComponent
              productId={showProductId ? productId : ''}
              productSize={productSize}
              idSizeType={IdAndSizeType.ID_SIZE_FREE_CART_CARD}
              prefixText={prefixText}
              showProductId={showProductId}
            />
          </div>
          <div className={styles['price-product-container']}>
            <div className={styles['price-pvbv-container']}>
              <PriceComponent
                productPrice={productPrice}
                currency={currency}
                strikethroughPrice={strikethroughPrice}
                priceType={PriceType.FREE_PRODUCT_CARD}
                showZeroAsFree={showZeroAsFree}
              />
              <PVBVComponent
                pointValue={pointValue}
                businessVolume={businessVolume}
                pvbvType={PVBVType.PVBV_FREE_CART_CARD}
              />
            </div>
          </div>
        </div>
        <div className={styles['free-stock-status']}>
          {includesDerivedAvailabilityStockStatus(
            derivedAvailabilityStatus as DerivedAvailabilityStatusInternal,
            DerivedAvailabilityStatusComponentGroupKey.AVAILABLE_IN_FEW_STOCKS,
          ) && (
            <StockStatusComponent
              derivedAvailabilityStatus={derivedAvailabilityStatus}
              dateSubText={dateSubText}
              firstAvailableFutureDate={firstAvailableFutureDate}
              showSubText
              stockStatusType={StockStatusComponentType.STOCK_STATUS_SEARCH_CARD}
              showPrefix={false}
            />
          )}
        </div>
        <div className={styles['free-card-bottom-container']}>
          {!isNaN(productQuantity as number) && (
            <ProductQuantityComponent
              productQuantity={productQuantity}
              productQuantityType={ProductQuantityType.FREE_PRODUCT_CARD}
            />
          )}
          {showRemoveIcon && (
            <FAIcon
              iconName="trash-can"
              onClick={() => onRemoveProduct?.(productId)}
              className={styles['delete-container']}
              data-testid="remove-icon-test-id"
            />
          )}
        </div>
        {showCancelIcon && (
          <button
            className={styles['cancel-container']}
            onClick={() => onRemoveProduct?.(productId)}
            onKeyDown={handleKeyDownRemove}
            data-testid="cart-card-cancel-cross-icon"
          >
            <FAIcon iconName={'xmark'} iconStyle={IconStyle.REGULAR} />
          </button>
        )}
      </div>
    </div>
  );
};

const GCOCardView = (image: ImageFn, basicDetailsAndSize: BasicDetailsAndSizeType) => (
  <div className={styles['cpw-product-card-container']} data-testid="gco-card-container-test-id">
    <div className={styles['product-image']} data-testid="gco-card-image-test-id">
      {image('')}
    </div>
    <div className={styles['product-description']} data-testid="gco-card-product-test-id">
      {basicDetailsAndSize(
        DetailsType.AllPRODUCTCARD,
        PVBVType.PVBVCART,
        IdAndSizeType.IDSIZEPRODUCTMERCHANDISE,
        PriceType.PRICE_GCO_CARD,
      )}
    </div>
  </div>
);

const CheckoutCardView = ({
  image,
  basicDetails,
  productId,
  showProductId,
  productPrice,
  productSize,
  pointValue,
  businessVolume,
  minPrice,
  maxPrice,
  isBundle,
  variantDropDown,
  cardType,
  derivedAvailabilityStatus,
  currency,
  strikethroughPrice,
  productQuantity,
  stockStatusEnabled,
  className,
  showZeroAsFree,
  orderInfoText,
  dateSubText,
  firstAvailableFutureDate,
  isUnavailable,
  unavailableText,
  showError,
  availabilityStatus,
  showRetailPrice,
  productRetailPrice,
  minRetailPrice,
  maxRetailPrice,
  showPrefix,
  itemIdPrefixText,
}: CheckoutCardType) => (
  <div
    className={`${styles['cpw-product-card-container']} ${styles['checkout-card-view']} ${
      stockStatusEnabled && styles['height-extension']
    } ${className}`}
    data-testid="checkout-card-container-testid"
  >
    <div className={styles['product-image']} data-testid="checkout-card-image-testid">
      {image(ImageType.CHECKOUT_CARD)}
    </div>
    <div className={styles['product-description']} data-testid="checkout-card-description-testid">
      {basicDetails(DetailsType.CHECKOUT_CARD)}
      <ProductIdAndSizeComponent
        productId={productId}
        showProductId={showProductId}
        productSize={productSize}
        idSizeType={IdAndSizeType.IDSIZECHECKOUT}
        prefixText={itemIdPrefixText}
      />
      {orderInfoText && <div className={styles['order-info']}>{orderInfoText}</div>}
      <div className={styles['cpw-product-price-qty']}>
        {cardType !== CardType.FREEPRODUCT && (
          <ConditionalPriceRangeComponent
            productPrice={productPrice as number}
            currency={currency as string}
            minPrice={minPrice}
            maxPrice={maxPrice}
            isBundle={isBundle}
            strikethroughPrice={strikethroughPrice}
            condionalPriceType={PriceType.CHECKOUT}
            showZeroAsFree={showZeroAsFree}
          />
        )}
        {!isNaN(productQuantity as number) && <ProductQuantityComponent productQuantity={productQuantity} />}
      </div>
      {showRetailPrice && (
        <ConditionalRetailPriceRangeComponent
          isBundle={isBundle}
          minPrice={minRetailPrice as number}
          maxPrice={maxRetailPrice as number}
          currency={currency}
          productPrice={productRetailPrice as number}
          condionalPriceType={PriceType.CHECKOUT}
          isRetailPrice={true}
        />
      )}
      <PVBVComponent pointValue={pointValue} businessVolume={businessVolume} pvbvType={PVBVType.PVBVCHECKOUT} />
      {derivedAvailabilityStatus && (
        <StockStatusComponent
          derivedAvailabilityStatus={derivedAvailabilityStatus}
          dateSubText={dateSubText}
          firstAvailableFutureDate={firstAvailableFutureDate}
          showSubText={false}
          showPrefix={showPrefix}
          stockStatusType={StockStatusComponentType.CHECKOUT_CARD}
        />
      )}
      {isUnavailable && (
        <div className={`${styles['unavailable-text']}  ${styles[showError ? 'error' : 'warning']}`}>
          {unavailableText}
        </div>
      )}
      {availabilityStatus && <p className={styles['availability-status']}>{availabilityStatus}</p>}
      {variantDropDown(DropdownButtonTypeEnum.CHECKOUT)}
    </div>
  </div>
);

const PLPHeaderCardView = (image: ImageFn, basicDetailsAndSize: BasicDetailsAndSizeType) => (
  <div
    className={`${styles['cpw-product-card-container']} ${styles['plp-header-container']}`}
    data-testid="plp-header-container-testID"
  >
    <div>{image(ImageType.PLPHEADERCARD)}</div>
    <div
      className={`${styles['product-description']} ${styles['plp-header-description']}`}
      data-testid="plp-header-description-testID"
    >
      {basicDetailsAndSize(DetailsType.PLPHEADERCARD, PVBVType.PVBVPDP, IdAndSizeType.IDSIZEDISPLAYNONE)}
    </div>
  </div>
);

const QtyStepperView = ({
  image,
  basicDetailsAndSize,
  stockStatusComp,
  onQuantityIncrease,
  onQuantityDecrease,
  derivedDisabled,
  productId,
  productCount,
  maxCount,
  className,
}: QtyStepperType) => (
  <div
    className={`${styles['cpw-product-card-container']} ${styles['qty-stepper-container-height']} ${className}`}
    data-testid="QtyStepper-container-testID"
  >
    <div
      className={`${styles['product-image']} ${styles['qty-stepper-image-height']}`}
      data-testid="QtyStepper-image-testID"
    >
      {image('')}
    </div>
    <div
      className={`${styles['product-description']} ${styles['dropdown-qty-stepper-tablet-width']}`}
      data-testid="QtyStepper-description-testID"
    >
      {basicDetailsAndSize(DetailsType.AllPRODUCTCARD, PVBVType.PVBVPDP, IdAndSizeType.IDSIZEPRODUCTCARD)}
      {stockStatusComp}
      <ProductButton
        onQuantityIncrease={onQuantityIncrease}
        onQuantityDecrease={onQuantityDecrease}
        disabled={derivedDisabled}
        productCount={productCount}
        maxCount={maxCount}
        id={productId}
        productButtonType={ProductButtonType.QTY_STEPPER}
      />
    </div>
  </div>
);

const NormalViewWithoutSize = ({
  image,
  basicDetails,
  stockStatusComp,
  className,
  stockStatusEnabled,
  productQuantity,
}: NormalCardWithoutSizeType) => (
  <div
    className={`${styles['cpw-product-card-container']} ${styles['container-without-size']}  ${
      stockStatusEnabled && styles['height-extension']
    } ${className}`}
    data-testid="normal-ws-container-test-id"
  >
    <div
      className={`${styles['product-image']} ${styles['normal-view-ws-image-height']}`}
      data-testid="normal-ws-image-test-id"
    >
      {image('')}
    </div>
    <div className={styles['product-description']} data-testid="normal-ws-description-test-id">
      {basicDetails(DetailsType.AllPRODUCTCARD)}
      {!isNaN(productQuantity as number) && (
        <ProductQuantityComponent
          productQuantity={productQuantity}
          productQuantityType={ProductQuantityType.AllPRODUCTCARD}
        />
      )}
      {stockStatusComp}
    </div>
  </div>
);

const DropDownView = ({
  image,
  basicDetailsAndSize,
  variantDropDown,
  stockStatusComp,
  pvbvViewType,
  idSizeType,
  priceType,
  stockStatusEnabled,
  className,
  productQuantity,
  prefixText,
}: DropdownType) => (
  <div
    className={`${styles['cpw-product-card-container']} ${styles['dropdown-container-height']} ${
      stockStatusEnabled && styles['height-extension']
    } ${className}`}
    data-testid="Dropdown-container-testID"
  >
    <div
      className={`${styles['product-image']} ${styles['dropdown-image-height']} `}
      data-testid="Dropdown-image-testID"
    >
      {image('')}
    </div>
    <div
      className={`${styles['product-description']} ${styles['dropdown-qty-stepper-tablet-width']}`}
      data-testid="Dropdown-description-testID"
    >
      <div className={styles['sub-description-container']}>
        {basicDetailsAndSize(DetailsType.AllPRODUCTCARD, pvbvViewType, idSizeType, priceType, prefixText)}
        {!isNaN(productQuantity as number) && <ProductQuantityComponent productQuantity={productQuantity} />}
        {stockStatusComp}
      </div>
      <div className={styles['horizontal-dropdown-cta-container']}>
        {variantDropDown(DropdownButtonTypeEnum.PRODUCT_CARD)}
      </div>
    </div>
  </div>
);

const ProductMerchandiseView = ({
  image,
  basicDetailsAndSize,
  variantDropDown,
  stockStatusComp,
  pvbvViewType,
  idSizeType,
  priceType,
  imageStyleType,
  stockStatusEnabled,
  className,
  productQuantity,
  buttonEle,
  prefixText,
  bundleCase,
  isUserTypeMatched,
  productTags,
  showTags,
}: ProductMerchandiseType) => {
  const { label = '', type = '' } = productTags || {};

  return (
    <div
      className={`${styles['cpw-product-card-container']} ${styles['dropdown-container-height']} ${
        stockStatusEnabled && styles['height-extension']
      } ${className} ${styles['product-merchandise-view']}`}
      data-testid="Dropdown-container-testID"
    >
      <div className={styles['tag-and-image-container']}>
        {showTags && getTagLabel(label, bundleCase, isUserTypeMatched) && (
          <div className={styles['cpw-tag-container']} data-testid="tag-container-test-id">
            <Tag
              className={styles['cpw-tag']}
              text={getTagLabel(label, bundleCase, isUserTypeMatched)}
              style={TagStyle.OUTLINE}
              theme={calculateTheme(getTagLabel(type, bundleCase.toLocaleUpperCase(), isUserTypeMatched))}
            />
          </div>
        )}
        <div className={`${styles['product-image']}`} data-testid="Dropdown-image-testID">
          {image(imageStyleType)}
        </div>
      </div>
      <div
        className={`${styles['product-description']} ${styles['dropdown-qty-stepper-tablet-width']}`}
        data-testid="Dropdown-description-testID"
      >
        <div className={styles['sub-description-container']}>
          {basicDetailsAndSize(DetailsType.HORIZONTAL_CARD_DETAILS, pvbvViewType, idSizeType, priceType, prefixText)}
          {!isNaN(productQuantity as number) && <ProductQuantityComponent productQuantity={productQuantity} />}
          {stockStatusComp}
        </div>
        <div className={styles['horizontal-dropdown-cta-container']}>
          {variantDropDown(DropdownButtonTypeEnum.PRODUCT_MERCHANDISE)}
          {buttonEle}
        </div>
      </div>
    </div>
  );
};

const NormalView = ({
  image,
  basicDetailsAndSize,
  stockStatusComp,
  pvbvViewType,
  idSizeType,
  priceType,
  imageStyleType,
  basicDetailsStyleType,
  stockStatusEnabled,
  className,
  productQuantity,
  productQuantityType,
  isUnavailable,
  unavailableText,
  showError,
}: NormalCardType) => (
  <div
    className={`${styles['cpw-product-card-container']} ${stockStatusEnabled && styles['height-extension']} ${className}`}
    data-testid="Normal-container-testID"
  >
    <div className={styles['product-image']} data-testid="Normal-image-testID">
      {image(imageStyleType)}
    </div>
    <div className={styles['product-description']} data-testid="Normal-description-testID">
      {basicDetailsAndSize(basicDetailsStyleType, pvbvViewType, idSizeType, priceType)}
      {!isNaN(productQuantity as number) && (
        <ProductQuantityComponent productQuantity={productQuantity} productQuantityType={productQuantityType} />
      )}
      {stockStatusComp}
      {isUnavailable && (
        <div className={`${styles['unavailable-text']}  ${styles[showError ? 'error' : 'warning']}`}>
          {unavailableText}
        </div>
      )}
    </div>
  </div>
);

export const ProductCardComponent: FC<ProductCardComponentPropsTypes> = ({
  link,
  alt,
  brandName,
  productName,
  productSize,
  productCount,
  variantName = '',
  disabled,
  productId,
  dateSubText,
  showSubText,
  firstAvailableFutureDate,
  maxCount,
  productQuantity,
  productPrice,
  strikethroughPrice,
  currency,
  onQuantityDecrease,
  onQuantityIncrease,
  handleDropdown,
  isBundle = false,
  minPrice = 0,
  maxPrice = 0,
  cardType = CardType.NORMAL,
  pointValue = '',
  businessVolume = '',
  className,
  availabilityStatus,
  showLinkForProductCard = true,
  derivedAvailabilityStatus,
  prefixText,
  showProductId = true,
  wishListEle,
  buttonEle,
  onRemoveProduct,
  showPropertiesCollection: {
    showCancelIcon = false,
    showPromotionTag = false,
    showRetailPrice = false,
    showRemoveIcon = false,
  } = {},
  orderInfoText,
  productType,
  priceType,
  isUnavailable,
  unavailableText,
  showZeroAsFree,
  showError = true,
  productRetailPrice = 0,
  maxRetailPrice = 0,
  minRetailPrice = 0,
  showPrefix = false,
  productTags = {},
  showTags = true,
  cancelledQuantity,
  itemIdPrefixText,
}): JSX.Element => {
  const t = useTranslation();
  const { session, isAnonymous, isABO } = useSession();
  const derivedDisabled =
    disabled ||
    ([DerivedAvailabilityStatusInternal.Nya, DerivedAvailabilityStatusInternal.Naw] as string[]).includes(
      derivedAvailabilityStatus || '',
    );
  const handleDropdownEvent = (e: React.KeyboardEvent | React.MouseEvent) => {
    if (handleDropdown && typeof handleDropdown === 'function') {
      handleDropdown(e);
    }
  };
  const [stockStatusEnabled, setStockStatusEnabled] = useState(false);
  const conditionalProductId = showProductId ? productId : '';

  const productLinkProps = {
    className: styles['width-inherit'],
    showLink: showLinkForProductCard,
    href: '',
    productId,
    productName,
  };

  const image = (type: string) => (
    <ProductLink {...productLinkProps}>
      <div className={`${styles['product-image-container']} ${styles['width-inherit']}`}>
        {showPromotionTag && (
          <Tag
            className={styles['promotion-tag']}
            icon={{
              iconName: 'tags',
            }}
            style={TagStyle.SOLID}
            tagWidth={TagWidth.FIT_CONTENT}
            text=""
            theme={Theme.SUCCESS}
          />
        )}
        <ImageComponent link={link} alt={alt} imagetype={type} />
      </div>
    </ProductLink>
  );

  const basicDetails = (type: string, mainText = brandName, subText = productName) => (
    <div className={styles['cpw-basic-details']}>
      <ProductLink {...productLinkProps}>
        <BasicDetailsComponent brandName={mainText} productName={subText} detailsType={type} />
      </ProductLink>
      {wishListEle}
    </div>
  );

  const stockStatusComp = (
    <StockStatusComponent
      derivedAvailabilityStatus={derivedAvailabilityStatus}
      stockStatusType={
        cardType === CardType.PRODUCTMERCHANDISE
          ? StockStatusComponentType.STOCK_STATUS_HORIZONTAL_CARD_COMPONENT
          : StockStatusComponentType.STOCK_STATUS_CARD_COMPONENT
      }
      firstAvailableFutureDate={firstAvailableFutureDate}
      dateSubText={dateSubText}
      showSubText={showSubText}
      cancelledQuantity={productQuantity == cancelledQuantity ? undefined : cancelledQuantity}
    />
  );

  const variantDropDown = (variant?: string) =>
    variantName &&
    !isBundle &&
    productType === ProductType.Variant && (
      <DropdownButton
        label={variantName}
        dropdownEventHandler={handleDropdownEvent}
        disabled={derivedDisabled}
        dropdownType={variant}
      />
    );

  useEffect(() => {
    if (
      derivedAvailabilityStatus &&
      [DerivedAvailabilityStatus.OutOfStock as string].includes(derivedAvailabilityStatus)
    ) {
      setStockStatusEnabled(true);
    } else {
      setStockStatusEnabled(false);
    }
  }, [stockStatusEnabled, derivedAvailabilityStatus]);

  const basicDetailsAndSize = (
    BasicDetailstype: string,
    PvBvType: string,
    idSizeType: string,
    condionalPriceType?: string,
    prefixText?: string,
  ) => (
    <>
      {basicDetails(BasicDetailstype)}
      <ProductIdAndSizeComponent
        productId={conditionalProductId}
        productSize={productSize}
        idSizeType={idSizeType}
        prefixText={prefixText}
      />
      {cardType !== CardType.FREEPRODUCT && (
        <ConditionalPriceRangeComponent
          productPrice={productPrice as number}
          currency={currency as string}
          minPrice={minPrice}
          maxPrice={maxPrice}
          isBundle={isBundle}
          strikethroughPrice={strikethroughPrice}
          condionalPriceType={condionalPriceType}
          showZeroAsFree={showZeroAsFree}
        />
      )}
      {cardType === CardType.PRODUCTMERCHANDISE && (
        <ConditionalRetailPriceRangeComponent
          isBundle={isBundle}
          minPrice={minRetailPrice as number}
          maxPrice={maxRetailPrice as number}
          currency={currency as string}
          productPrice={productRetailPrice as number}
          condionalPriceType={condionalPriceType}
          isRetailPrice={true}
        />
      )}
      <PVBVComponent pointValue={pointValue} businessVolume={businessVolume} pvbvType={PvBvType} />
    </>
  );

  const PDPHeaderCard = () =>
    PDPHeaderCardView({
      productId,
      stockStatusEnabled,
      image,
      basicDetailsAndSize,
      stockStatusComp,
      productQuantity,
      className,
    });

  const QtyStepper = () =>
    QtyStepperView({
      image,
      basicDetailsAndSize,
      stockStatusComp,
      onQuantityIncrease,
      onQuantityDecrease,
      derivedDisabled,
      productId,
      productCount,
      maxCount,
      className,
    });

  const PLPHeaderCard = () => PLPHeaderCardView(image, basicDetailsAndSize);

  const CheckoutCard = () =>
    CheckoutCardView({
      image,
      basicDetails,
      productId,
      showProductId,
      productPrice,
      productSize,
      pointValue,
      businessVolume,
      minPrice,
      maxPrice,
      isBundle,
      variantDropDown,
      cardType,
      derivedAvailabilityStatus,
      currency,
      strikethroughPrice,
      productQuantity,
      stockStatusEnabled,
      className,
      showZeroAsFree,
      orderInfoText,
      dateSubText,
      firstAvailableFutureDate,
      isUnavailable,
      unavailableText,
      availabilityStatus,
      showError,
      showRetailPrice,
      productRetailPrice,
      maxRetailPrice,
      minRetailPrice,
      showPrefix,
      itemIdPrefixText,
    });

  const GCOCard = () => GCOCardView(image, basicDetailsAndSize);

  const ProductMerchandiseCard = () => {
    const bundleCase = isBundle ? t('product.common.bundle') : '';
    const { userTypes = [] } = productTags || {};
    let userTypeMatchFlag = true;
    if (userTypes?.length > 0) {
      let userType = '';
      if (isAnonymous()) {
        userType = UserType.REGISTERED;
      } else if (isABO()) {
        userType = UserTypeEnums.ABO;
      } else {
        userType = session?.type || '';
      }
      userTypeMatchFlag = userTypes.includes(userType as UserSegment);
    }
    return ProductMerchandiseView({
      image,
      basicDetailsAndSize,
      variantDropDown,
      stockStatusComp,
      pvbvViewType: PVBVType.PVBVPRODUCTMERCHANDISE,
      idSizeType: IdAndSizeType.IDSIZEPRODUCTMERCHANDISE,
      priceType: PriceType.PRODUCT_MERCHANDISE,
      imageStyleType: ImageType.HORIZONTAL_CARD,
      stockStatusEnabled,
      className,
      productQuantity,
      buttonEle,
      prefixText,
      bundleCase,
      isUserTypeMatched: userTypeMatchFlag,
      productTags,
      showTags,
    });
  };

  const DropdownCard = () =>
    DropDownView({
      image,
      basicDetailsAndSize,
      variantDropDown,
      stockStatusComp,
      pvbvViewType: PVBVType.PVBVPDP,
      idSizeType: IdAndSizeType.IDSIZEPRODUCTCARD,
      priceType,
      stockStatusEnabled,
      className,
      productQuantity,
    });

  const PromotionCard = () =>
    PromotionCardView({
      image,
      basicDetailsAndSize,
      stockStatusComp,
      className,
      buttonEle,
    });

  const FreeProductCard = () =>
    FreeProductCardView({
      image,
      basicDetails,
      showProductId,
      productSize,
      productId,
      productPrice: productPrice as number,
      currency,
      strikethroughPrice,
      productQuantity,
      onRemoveProduct,
      showCancelIcon,
      prefixText,
      className,
      pointValue,
      businessVolume,
      showZeroAsFree,
      derivedAvailabilityStatus,
      dateSubText,
      firstAvailableFutureDate,
      showRemoveIcon,
    });

  const NormalCard = () =>
    NormalView({
      image,
      basicDetailsAndSize,
      stockStatusComp,
      pvbvViewType: CardType.BUNDLE_CARD ? PVBVType.PVBVCHECKOUT : PVBVType.PVBVPDP,
      idSizeType: conditionalProductId ? IdAndSizeType.IDSIZEPRODUCTCARD : IdAndSizeType.IDSIZECART,
      priceType,
      imageStyleType: '',
      basicDetailsStyleType: DetailsType.AllPRODUCTCARD,
      stockStatusEnabled,
      className,
      productQuantity,
      productQuantityType: '',
      isUnavailable,
      unavailableText,
      showError,
    });

  const NormalCardWithoutSize = () =>
    NormalViewWithoutSize({
      image,
      basicDetails,
      stockStatusComp,
      className,
      stockStatusEnabled,
      productQuantity,
    });

  const CartCard = () =>
    NormalView({
      image,
      basicDetailsAndSize,
      stockStatusComp,
      pvbvViewType: PVBVType.PVBVPDP,
      idSizeType: conditionalProductId ? IdAndSizeType.IDSIZEPRODUCTCARD : IdAndSizeType.IDSIZECART,
      priceType,
      imageStyleType: ImageType.CART_CARD,
      basicDetailsStyleType: DetailsType.CART_CARD_VARIANT,
      stockStatusEnabled,
      className,
      productQuantity,
      productQuantityType: ProductQuantityType.CART_CARD_VARIANT,
      isUnavailable,
      unavailableText,
    });

  const loadView = () => {
    switch (cardType) {
      case CardType.DROPDOWN:
        return DropdownCard();
      case CardType.QTYSTEPPER:
        return QtyStepper();
      case CardType.NORMAL:
      case CardType.BUNDLE_CARD:
        return NormalCard();
      case CardType.CART_CARD:
        return CartCard();
      case CardType.PDPHEADERTYPE:
        return PDPHeaderCard();
      case CardType.FREEPRODUCT:
        return FreeProductCard();
      case CardType.CHECKOUT:
        return CheckoutCard();
      case CardType.PLPHEADER:
        return PLPHeaderCard();
      case CardType.PRODUCTMERCHANDISE:
        return ProductMerchandiseCard();
      case CardType.PROMOTIONCARD:
        return PromotionCard();
      case CardType.GCOCARD:
        return GCOCard();
      default:
        return NormalCardWithoutSize();
    }
  };

  return <>{loadView()}</>;
};

export default ProductCardComponent;
export { CardType };
export type { ProductCardComponentPropsTypes };
